import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAppConfig } from '../../AppConfigProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faUser, faEdit, faTimes } from '@fortawesome/free-solid-svg-icons';
import '../../ModernTable.css';

export default function MyAccount({ uId }) {
    const appConfig = useAppConfig();
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [originalProfile, setOriginalProfile] = useState({
        fullname: '',
        email: '',
        phoneNo: '',
        companyName: ''
    });
    const [profile, setProfile] = useState({
        fullname: '',
        email: '',
        phoneNo: '',
        companyName: ''
    });

    useEffect(() => {
        if (!uId) return;
        axios.get(`${appConfig.backendUrl}api/v1/auth/me`)
            .then(res => {
                const data = {
                    fullname: res.data.fullname || '',
                    email: res.data.email || '',
                    phoneNo: res.data.phoneNo || '',
                    companyName: res.data.companyName || ''
                };
                setProfile(data);
                setOriginalProfile(data);
            })
            .catch(err => {
                console.error("Failed to load profile", err);
                toast.error("Failed to load profile details", { theme: "dark" });
            });
    }, [appConfig.backendUrl, uId]);

    const handleChange = (e) => {
        let { name, value } = e.target;
        if (name === 'phoneNo') {
            value = value.replace(/\D/g, ''); // Keep only digits
            if (value.length > 11) {
                toast.warn("Mobile number cannot exceed 11 digits", { 
                    theme: "dark", 
                    autoClose: 2000,
                    toastId: "phone-limit" 
                });
                value = value.slice(0, 11);
            }
        }
        setProfile({ ...profile, [name]: value });
    };

    const handleKeyDown = (e) => {
        if ([46, 8, 9, 27, 13, 110].indexOf(e.keyCode) !== -1 ||
            (e.keyCode === 65 && (e.ctrlKey === true || e.metaKey === true)) || 
            (e.keyCode >= 35 && e.keyCode <= 40)) {
                 return;
        }
        if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
            e.preventDefault();
        }
    };

    const handleCancel = () => {
        setProfile(originalProfile);
        setIsEditing(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (profile.phoneNo && (profile.phoneNo.length < 10 || profile.phoneNo.length > 11)) {
            toast.error("Please enter a valid mobile number (10-11 digits)", { theme: "dark" });
            return;
        }

        setLoading(true);
        try {
            const response = await axios.put(`${appConfig.backendUrl}api/v1/auth/profile`, profile);
            toast.success(response.data.message || "Profile updated successfully!", {
                theme: "dark"
            });
            setOriginalProfile(profile);
            setIsEditing(false);
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || "Failed to update profile", {
                theme: "dark"
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container" style={{ paddingTop: '80px', minHeight: '100vh', paddingBottom: '30px' }}>
            <div className="row justify-content-center">
                <div className="col-xl-6 col-lg-8 col-md-10">
                    <div className="page-header">
                        <h1 className="page-title">
                            <FontAwesomeIcon icon={faUser} />
                            My Account
                        </h1>
                        <p className="page-subtitle">Manage your personal and company details</p>
                    </div>

                    {!isEditing ? (
                        <div className="card shadow-sm border-0 rounded-4">
                            <div className="card-body p-4 p-md-5">
                                <div className="mb-4">
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center px-0 py-3 border-bottom">
                                        <span className="text-muted fw-bold mb-1 mb-sm-0" style={{ fontSize: '0.85rem', letterSpacing: '1px', textTransform: 'uppercase' }}>Full Name</span>
                                        <span className="fw-bold" style={{ color: '#2c3e50', fontSize: '1.1rem' }}>{profile.fullname || '-'}</span>
                                    </div>
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center px-0 py-3 border-bottom">
                                        <span className="text-muted fw-bold mb-1 mb-sm-0" style={{ fontSize: '0.85rem', letterSpacing: '1px', textTransform: 'uppercase' }}>Email Address</span>
                                        <span className="fw-bold" style={{ color: '#2c3e50', fontSize: '1.1rem' }}>{profile.email || '-'}</span>
                                    </div>
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center px-0 py-3 border-bottom">
                                        <span className="text-muted fw-bold mb-1 mb-sm-0" style={{ fontSize: '0.85rem', letterSpacing: '1px', textTransform: 'uppercase' }}>Phone Number</span>
                                        <span className="fw-bold" style={{ color: '#2c3e50', fontSize: '1.1rem' }}>{profile.phoneNo || '-'}</span>
                                    </div>
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center px-0 py-3 border-bottom">
                                        <span className="text-muted fw-bold mb-1 mb-sm-0" style={{ fontSize: '0.85rem', letterSpacing: '1px', textTransform: 'uppercase' }}>Company Name</span>
                                        <span className="fw-bold" style={{ color: '#2c3e50', fontSize: '1.1rem' }}>{profile.companyName || '-'}</span>
                                    </div>
                                </div>
                                <div className="text-center d-flex justify-content-center myAccountEdit">
                                    <button onClick={() => setIsEditing(true)} className="btn-premium primary w-100 shadow-sm" style={{ transition: 'all 0.3s', fontSize: '1rem', maxWidth: '300px', height: '44px', borderRadius: '50px', padding: '0 20px', margin: '0 auto' }}>
                                        <FontAwesomeIcon icon={faEdit} className="me-2" />
                                        Edit Profile
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="card shadow-sm border-0 rounded-4">
                            <div className="card-body p-4 p-md-5 myAccountEdit">
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-4">
                                        <label className="form-label text-muted fw-bold ms-1" style={{ fontSize: '0.9rem' }}>Full Name</label>
                                        <input type="text" className="form-control btn-radius btn-input py-2" name="fullname" value={profile.fullname} onChange={handleChange} required />
                                    </div>
                                    <div className="mb-4">
                                        <label className="form-label text-muted fw-bold ms-1" style={{ fontSize: '0.9rem' }}>Email Address</label>
                                        <input type="email" className="form-control btn-radius btn-input py-2" name="email" value={profile.email} onChange={handleChange} required />
                                    </div>
                                    <div className="mb-4">
                                        <label className="form-label text-muted fw-bold ms-1" style={{ fontSize: '0.9rem' }}>Phone Number (Optional)</label>
                                        <input
                                            type="tel"
                                            className="form-control btn-radius btn-input py-2"
                                            name="phoneNo"
                                            value={profile.phoneNo}
                                            onChange={handleChange}
                                            onKeyDown={handleKeyDown}
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            maxLength={11}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <label className="form-label text-muted fw-bold ms-1" style={{ fontSize: '0.9rem' }}>Company Name (Optional)</label>
                                        <input type="text" className="form-control btn-radius btn-input py-2" name="companyName" value={profile.companyName} onChange={handleChange} />
                                    </div>
                                    <div className="mt-5 d-flex gap-3">
                                        <button type="button" onClick={handleCancel} className="btn-premium danger w-50 shadow-sm" style={{ transition: 'all 0.3s', fontSize: '1rem', height: '44px', borderRadius: '50px' }} disabled={loading}>
                                            <FontAwesomeIcon icon={faTimes} className="me-2" />
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn-premium primary w-50 shadow-sm" style={{ transition: 'all 0.3s', fontSize: '1rem', height: '44px', borderRadius: '50px' }} disabled={loading}>
                                            <FontAwesomeIcon icon={faSave} className="me-2" />
                                            {loading ? 'Saving...' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <ToastContainer />
        </div>
    );
}
